//Proofed
import { useState } from 'react'
import { Box, Container, Grid, Typography, Stack, Chip } from '@mui/material'
import { Link } from 'react-router-dom'
import { BookDemoModal } from '../../components/BookDemoModal'
import { FancyGoldButton } from '../../components/FancyGoldButton'
import { FancyLightButton } from '../../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import { FaqSection } from '../../components/FaqSection'
import StarIcon from '@mui/icons-material/Star'
import { COLOR_QUATERNARY, EXECUTION_HERO_START, EXECUTION_HERO_MID, CHECKCIRCLE_SIZE, SECTION_DARK, DARK_800, BG_LIGHT, NAVBAR_BG } from '../../theme'
import { HowItWorksSection } from '../../components/HowItWorksSection'
import { TrustedPartnerSection } from '../../components/TrustedPartnerSection'
import { WaveDivider } from '../../components/WaveDivider'
import { ChallengesSection } from '../../components/ChallengesSection'
import { SolutionStack } from '../../components/SolutionStack'
import { CtaSection } from '../../components/CtaSection'
import PageMeta from '../../components/PageMeta'
import { RelatedSolutionsSection } from '../../components/RelatedSolutionsSection'
import { OutcomesSection } from '../../components/OutcomesSection'
import { SelfQualificationSection } from '../../components/SelfQualificationSection'
import {
    efficiencyHighlights,
    efficiencyChallenges,
    efficiencySolutionStack,
    efficiencySteps,
    efficiencyOutcomes,
    efficiencyQualifiers,
    efficiencyFaqs,
    efficiencyRelated,
} from '../../common/SolutionEfficiencyContent'
// import { efficiencyTestimonials } from '../../common/Testimonials'

const C = COLOR_QUATERNARY

export default function ImproveEfficiency() {
    const [demoOpen, setDemoOpen] = useState(false)

    return (
        <Box>
            <PageMeta
                title="Improve Efficiency and Win Rates"
                description="Qualify opportunities faster, cut bid writing time with AI and use market analytics to understand buyers before you write a word. CFP gives you the tools to bid smarter and win more."
                canonical="/solutions/improve-efficiency-and-win-rates"
            />
            {/* ── 1. Hero  light left / dark panel right ─────────────────────── */}
            <Box sx={{
                background: `linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`,
                py: { xs: 6, md: 10 },
            }}>
                <Container maxWidth="lg">


                    <Grid container spacing={5} alignItems="stretch">
                        {/* Left  copy */}
                        <Grid item xs={12} md={6}>
                            <Chip label="Execution Support · Performance" size="small" sx={{
                                mb: 3, backgroundColor: `${C}12`, color: C,
                                border: `1px solid ${C}40`, fontWeight: 700,
                                fontSize: 18,
                            }} />
                            <Typography variant="h1" sx={{ fontSize: { xs: '36px', md: '52px' }, mb: 2, color: NAVBAR_BG, lineHeight: 1.15 }}>
                                Bid Smarter, Win More{' '}
                                <Box component="span" sx={{ color: C }}>Waste Less</Box>
                            </Typography>
                            <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 400, mb: 4, lineHeight: 1.6 }}>
                                You're already in the market and submitting bids but your win rate could be higher, your process more efficient and your pipeline more strategic.
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <FancyGoldButton to="/pricing" color={C} endIcon={<ArrowForwardIcon />}>
                                    Get Started
                                </FancyGoldButton>
                                <FancyLightButton onClick={() => setDemoOpen(true)} color={C}>
                                    Book a Discovery Call
                                </FancyLightButton>
                            </Stack>
                        </Grid>

                        {/* Right  dark highlights panel */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                background: `linear-gradient(135deg, ${EXECUTION_HERO_START} 0%, ${EXECUTION_HERO_MID} 100%)`,
                                border: `1px solid ${C}30`,
                                borderRadius: 3,
                                p: { xs: 3, md: 4 },
                                // height: '100%',
                            }}>
                                <Typography variant="caption" sx={{
                                    color: C, textTransform: 'uppercase',
                                    letterSpacing: '1px', fontWeight: 700,
                                }}>
                                    With this solution you'll be able to
                                </Typography>
                                <Stack spacing={1.75} sx={{ mt: 2.5 }}>
                                    {efficiencyHighlights.map(h => (
                                        <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <CheckCircleIcon sx={{ color: C, fontSize: CHECKCIRCLE_SIZE, mt: 0.15, flexShrink: 0 }} />
                                            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', lineHeight: 1.6 }}>{h}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <WaveDivider top={`linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`} bottom={DARK_800} />
            {/* ── 2. Challenges  dark ─────────────────────────────────────────── */}
            <ChallengesSection
                title="Three inefficiencies keeping your win rate down"
                subtitle="These are the patterns we see most often in established bidders who aren't getting the results their capability justifies"
                items={efficiencyChallenges}
                color={C}
            />

            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* ── 3. Solution stack  light ────────────────────────────────────── */}
            <SolutionStack
                subtitle="Platform monitoring, AI bid writing and market analytics, all working together to give you a systematic competitive advantage across every bid decision"
                items={efficiencySolutionStack}
                color={C}
            />
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />

            {/* ── 4. Journey  dark (shared HowItWorksSection) ─────────────────── */}
            <HowItWorksSection
                title="Your route to higher performance"
                subtitle="Four steps from reactive bidding to a data driven BD operation that consistently outperforms"
                steps={efficiencySteps}
                dark={true}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* ── 5. Outcomes  light ──────────────────────────────────────────── */}
            <OutcomesSection
                subtitle="After deploying CFP Core Professional, Tender Ninja and CFP Analytics, this is the position you'll be in"
                items={efficiencyOutcomes}
                color={C}
            />
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 6. Self-qualification  dark ─────────────────────────────────── */}
            <SelfQualificationSection
                items={efficiencyQualifiers}
                color={C}
                onDemoClick={() => setDemoOpen(true)}
            />

            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* ── 9. Related solutions  light ─────────────────────────────────── */}
            <RelatedSolutionsSection
                subtitle="The platform grows with you. Here's where most high-performing bidders head next"
                items={efficiencyRelated}
                color={C}
            />
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 8. FAQ  dark ────────────────────────────────────────────────── */}
            <FaqSection
                subtitle="From established bidders who've asked these before investing in the full suite"
                items={efficiencyFaqs}
                color={DARK_800}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            <TrustedPartnerSection />
            <CtaSection
                title="Ready to bid smarter and win more?"
                subtitle="Book a no-obligation discovery call. We'll configure a trial of the full suite against your actual pipeline"
                onDemoClick={() => setDemoOpen(true)}
            />

            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    )
}
